import React, { useEffect } from 'react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Popper,
  Box,
  Typography,
  Divider,
  Paper,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import PopupState, { bindPopper, bindTrigger } from 'material-ui-popup-state'
import { useCreateTemplateMutation, useUpdateTemplateMutation } from '@/core/services/report'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useAppSelector } from '@/redux/rootReducer'
import DeleteIcon from '@mui/icons-material/Delete'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { ReportTemplate } from '@/core/models/report.model'
import { IconButton } from '@mui/material'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import _ from 'lodash'
import TextInputController from '@/components/ui/inputs/TextInputController'

interface CreateReportTemplateFormData {
  title: string
  description: string
  sections: { name: string; description: string }[]
  isDefault: boolean
  createdBy: string
}

type CreateReportTemplateProps = {
  isEdit?: boolean
  template?: ReportTemplate
}

export default function CreateReportTemplateModal({ isEdit = false, template }: CreateReportTemplateProps) {
  useLingui()
  const authUser = useAppSelector(selectAuthUser)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<CreateReportTemplateFormData>({
    defaultValues: {
      title: '',
      description: '',
      sections: [{ name: '', description: '' }],
      isDefault: false,
      createdBy: authUser?._id,
    },
  })

  const [createTemplate] = useCreateTemplateMutation()
  const [updateTemplate] = useUpdateTemplateMutation()

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'sections',
  })

  useEffect(() => {
    if (isEdit && template) {
      setValue('title', template.title)
      setValue('description', template.description)
      setValue('isDefault', template.isDefault)

      if (template.sections && template.sections.length > 0) {
        replace(template.sections)
      }
    }
  }, [isEdit, template, setValue, replace])

  return (
    <PopupState variant="popover" popupId="create-report-template-modal">
      {(popupState: any) => (
        <>
          {isEdit ? (
            <IconButton {...bindTrigger(popupState)} aria-label={t`Edit template`}>
              <VisibilityRoundedIcon htmlColor="#269CA6" />
            </IconButton>
          ) : (
            <Button variant="contained" color="primary" {...bindTrigger(popupState)} startIcon={<AddCircleOutlineIcon />}>
              <Trans>Create a new template</Trans>
            </Button>
          )}
          <Popper {...bindPopper(popupState)} sx={{ zIndex: 1300 }}>
            <Paper elevation={8}>
              <Dialog open onClose={popupState.close} maxWidth="sm">
                <DialogTitle>
                  <Typography variant="h5">{isEdit ? t`Edit template` : t`Create a new template`}</Typography>
                </DialogTitle>
                <DialogContent>
                  <form>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12}>
                        <TextInputController name="title" rules={{ required: t`Title is required` }} control={control} label={t`Title`} fullWidth />
                      </Grid>

                      <Grid item xs={12} sx={{ mt: 1 }}>
                        <TextInputController
                          rows={3}
                          name="description"
                          rules={{ required: t`Description is required` }}
                          control={control}
                          label={t`Description`}
                          multiline
                          fullWidth
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Box sx={{ mt: 2, mb: 1 }}>
                          <Typography variant="h6">
                            <Trans>Criteria</Trans>
                          </Typography>
                          <Divider />
                        </Box>

                        {fields.map((fieldItem, index) => (
                          <Grid container spacing={2} marginTop={1} key={fieldItem.id}>
                            <Grid item xs={12}>
                              <TextInputController
                                fullWidth
                                name={`sections.${index}.name`}
                                control={control}
                                label={t`Name`}
                                rules={{ required: t`Name is required` }}
                              />
                            </Grid>

                            <Grid item xs={12} sx={{ mt: 1 }}>
                              <TextInputController
                                name={`sections.${index}.description`}
                                control={control}
                                label={t`Description`}
                                multiline
                                rows={3}
                                fullWidth
                                rules={{ required: t`Description is required` }}
                              />
                            </Grid>
                            {fields.length > 1 && (
                              <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end">
                                  <Button color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => remove(index)} size="small">
                                    <Trans>Delete</Trans>
                                  </Button>
                                </Box>
                              </Grid>
                            )}
                          </Grid>
                        ))}
                      </Grid>
                      <Grid item>
                        <Controller
                          name="isDefault"
                          control={control}
                          render={({ field }) => <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label={t`Set as default`} />}
                        />
                      </Grid>
                    </Grid>
                  </form>
                </DialogContent>
                <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <Button variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={() => append({ name: '', description: '' })}>
                    <Trans>Add section</Trans>
                  </Button>

                  <Box>
                    <Button onClick={popupState.close} color="inherit" sx={{ mr: 1 }}>
                      <Trans>Cancel</Trans>
                    </Button>
                    <Button
                      onClick={handleSubmit(async (data: CreateReportTemplateFormData) => {
                        const reportTemplateData = {
                          ...data,
                        }

                        if (isEdit && template) {
                          await updateTemplate({ id: template._id, ...reportTemplateData }).unwrap()
                        } else {
                          await createTemplate(reportTemplateData).unwrap()
                        }
                        if (_.isEmpty(errors)) {
                          reset()
                          popupState.close()
                        }
                      })}
                      variant="contained"
                      color="primary"
                    >
                      {isEdit ? <Trans>Update</Trans> : <Trans>Create</Trans>}
                    </Button>
                  </Box>
                </DialogActions>
              </Dialog>
            </Paper>
          </Popper>
        </>
      )}
    </PopupState>
  )
}
